import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getNextCursor } from '@/api/cursorPagination';
import { fetchDeviceUserLevelKeyHistory, fetchDeviceUserLevelKeys } from '../api/deviceUserLevelKeys';
import type { VisibleUserLevel } from '../types';
import { SENSITIVE_QUERY_OPTIONS } from './sensitiveQueryOptions';

export const deviceUserLevelKeysKeys = {
  all: ['deviceUserLevelKeys'] as const,
  current: (deviceId: string) => [...deviceUserLevelKeysKeys.all, deviceId, 'current'] as const,
  history: (deviceId: string, userLevel: VisibleUserLevel) =>
    [...deviceUserLevelKeysKeys.all, deviceId, 'history', userLevel] as const,
};

// useDeviceUserLevelKeys loads the current keys for user levels 1-3. Superadmin only; pass
// enabled=false to skip the request when the feature flag is off.
export function useDeviceUserLevelKeys(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: deviceUserLevelKeysKeys.current(deviceId),
    queryFn: () => fetchDeviceUserLevelKeys(deviceId),
    enabled: Boolean(deviceId) && enabled,
    ...SENSITIVE_QUERY_OPTIONS,
  });
}

// useDeviceUserLevelKeyHistory pages through past keys of one user level. Its caller is mounted
// only while the history popup is open, so the query lives no longer than the popup.
export function useDeviceUserLevelKeyHistory(deviceId: string, userLevel: VisibleUserLevel) {
  return useInfiniteQuery({
    queryKey: deviceUserLevelKeysKeys.history(deviceId, userLevel),
    queryFn: ({ pageParam }) => fetchDeviceUserLevelKeyHistory(deviceId, userLevel, { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: getNextCursor,
    enabled: Boolean(deviceId),
    ...SENSITIVE_QUERY_OPTIONS,
  });
}
